import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {Box, LinearProgress, MenuItem, MenuList, Popover, Tooltip, Typography} from '@mui/material'
import type {FC, MouseEvent, ReactElement, ReactNode} from 'react'
import React, {useState} from 'react'
import {useOverlayMount} from '@hooks/useOverlayMount'
import ScrollbarContainer from '@shared/components/ScrollbarContainer'
import SimpleSearch from '@shared/components/SimpleSearch'
import Button from '@mui/material/Button'

interface BasicPopoverWithSearchProps {
  title: ReactNode
  tooltip?: string
  header?: string
  /** Rendered in the header slot, under `header`, for controls that scope the list. */
  headerContent?: ReactNode
  children: ReactElement | ReactElement[] | null
  onSearchItemChange: (searchItem: string) => void
  /**
   * Fired when the dropdown closes — by backdrop, Escape, or a selection that
   * dismisses it. The search input lives inside the popover, so it unmounts and
   * resets itself on close while whatever state the caller derived from
   * `onSearchItemChange` does not; this is the hook for putting the two back in
   * agreement.
   */
  onClose?: () => void
  /** Fired when the trigger opens the dropdown; the counterpart of `onClose`. */
  onOpen?: () => void
  width?: string
  buttonDataCy?: string
  buttonAriaDescribedBy?: string
  menuDataCy?: string
  disableButton?: boolean
  idProp?: string
  closeOnSelect?: boolean
  multiSelect?: boolean
  buttonWidth?: string
  minWidth?: string
  maxWidth?: string
  isLoading?: boolean
  leftIcon?: ReactElement
  topRightBatchIcon?: ReactElement
  size?: 'small' | 'medium' | 'large'
  allowTitleWrap?: boolean
  // Drop the search row. For a list that is both complete and short — three
  // statuses, two modes — the box is most of the popover and has nothing to
  // search. Opt-in: every other dropdown keeps it.
  hideSearch?: boolean
}

const BasicPopoverWithSearch: FC<BasicPopoverWithSearchProps> = ({
  title,
  tooltip,
  header,
  headerContent,
  children,
  onSearchItemChange,
  onClose,
  onOpen,
  width = '100px',
  buttonDataCy = 'mbai-basic-popover-with-search-button',
  buttonAriaDescribedBy,
  menuDataCy = 'mbai-basic-popover-with-search-menu',
  disableButton = false,
  idProp,
  closeOnSelect = true,
  multiSelect = false,
  buttonWidth = '100%',
  minWidth = '150px',
  maxWidth = '400px',
  isLoading = false,
  leftIcon = undefined,
  topRightBatchIcon = undefined,
  size,
  allowTitleWrap = false,
  hideSearch = false,
}): ReactElement => {
  // States
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // Functions
  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
    onOpen?.()
  }

  const handleClose = (): void => {
    setAnchorEl(null)
    onClose?.()
  }

  const handleSetSearchItem = (searchItem: string): void => {
    onSearchItemChange(searchItem)
  }

  const shouldCloseOnSelect = multiSelect ? false : closeOnSelect

  const open = Boolean(anchorEl)
  const {mounted: popoverMounted, onTransitionExited} = useOverlayMount(open)

  const finalTooltip = tooltip || (typeof title === 'string' ? title : '')

  return (
    <>
      <Tooltip title={finalTooltip} placement="top" arrow>
        <span>
          <Button
            onClick={handleClick}
            sx={{
              width: buttonWidth,
              minWidth,
              maxWidth,
              color: 'text.primary',
              // The theme capitalizes MuiButton, which title-cases whatever the
              // trigger is showing. That is wrong for the DATA these triggers
              // display: German function words ("1 Von 529 Läufen"), infinitives
              // ("Läufe Hinzufügen") and proper nouns / platform state names
              // ("Colour Deck 01 · On Impression (3)") all get mangled. The
              // dropdown rows are MenuItems and were always correct — this makes
              // the trigger agree with them.
              textTransform: 'none',
              '& .MuiButton-startIcon': {
                marginRight: 1,
              },
              backgroundColor: (theme) => theme.palette.background.fadedOpaque,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.background.paper,
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
                alignSelf: 'center',
              },
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              textAlign: 'left',
              minHeight: size === 'large' ? '43px' : undefined,
              height: allowTitleWrap ? 'auto' : size === 'large' ? '43px' : '100%',
              py: allowTitleWrap ? 0.75 : undefined,
            }}
            data-cy={buttonDataCy}
            aria-describedby={buttonAriaDescribedBy}
            data-testid="mbai-basic-popover-with-search-button"
            disabled={disableButton}
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            id={idProp}
            loading={isLoading}
          >
            {leftIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 25,
                  height: 25,
                  minWidth: 25,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.background.fadedOpaque,
                  marginRight: 1,
                }}
              >
                {React.cloneElement(leftIcon, {
                  sx: {
                    fontSize: 14,
                    ...(leftIcon.props.sx || {}),
                  },
                })}
              </Box>
            )}
            {topRightBatchIcon &&
              React.cloneElement(topRightBatchIcon, {
                sx: {
                  position: 'absolute',
                  top: -4,
                  right: -6,
                  fontSize: '1rem',
                  color: 'primary.main',
                  backgroundColor: 'background.paper',
                  borderRadius: '50%',
                  padding: '1px',
                  ...(topRightBatchIcon.props.sx || {}),
                },
              })}
            <Typography
              noWrap={!allowTitleWrap}
              component="span"
              sx={{
                textOverflow: allowTitleWrap ? 'unset' : 'ellipsis',
                overflow: allowTitleWrap ? 'visible' : 'hidden',
                display: 'block',
                textAlign: 'left',
                whiteSpace: allowTitleWrap ? 'normal' : 'nowrap',
                maxWidth: 'calc(100% - 32px)',
                flexGrow: 1,
                fontSize: '0.9rem',
              }}
            >
              {title}
            </Typography>
          </Button>
        </span>
      </Tooltip>
      {/* Mounted only while open (plus the exit): a Popover whose exit
          transition never reports back is left in the DOM as an invisible
          full-viewport layer that eats every click on the page — reported
          from the equipment signal list, which reaches this component
          through EquipmentToggleMain (see useOverlayMount, AB#6086). */}
      {popoverMounted ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          onTransitionExited={onTransitionExited}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              minWidth:
                width === 'auto'
                  ? anchorEl?.offsetWidth || parseInt(minWidth) || 150
                  : Math.max(
                      anchorEl?.offsetWidth || parseInt(minWidth) || 150,
                      parseInt(width) || 400,
                    ),
              maxWidth: maxWidth === '100%' ? 'none' : maxWidth,
            },
          }}
        >
          <MenuList
            sx={{
              '& .MuiButtonBase-root': {
                '&:not(:last-child)': {
                  borderBottom: 1,
                  borderColor: 'divider',
                },
              },
              p: 0,
            }}
            data-cy={menuDataCy}
          >
            {/* No maxWidth of its own: the paper already carries the cap, but it
                also grows to the trigger's width, and a trigger wider than the
                cap (a full-width field in a dialog) left this content stopped at
                the cap with an empty strip beside it (AB#6640). */}
            <Box
              sx={{
                pb: 1,
                pt: 0,
                minWidth: width,
              }}
            >
              {(header || headerContent) && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                  }}
                >
                  {header && <Typography variant="caption">{header}</Typography>}
                  {headerContent}
                </Box>
              )}
              {!hideSearch && (
                <MenuItem
                  key="search"
                  sx={{
                    p: 1,
                    '& > .MuiBox-root': {flexGrow: 1},
                  }}
                >
                  <SimpleSearch onSearch={handleSetSearchItem} size="small" />
                </MenuItem>
              )}
              {isLoading && <LinearProgress sx={{mx: 1, borderRadius: 1}} />}
              <ScrollbarContainer maxHeight="300px">
                <Box sx={{width: '100%'}} onClick={shouldCloseOnSelect ? handleClose : undefined}>
                  {children}
                </Box>
              </ScrollbarContainer>
            </Box>
          </MenuList>
        </Popover>
      ) : null}
    </>
  )
}

export default BasicPopoverWithSearch
